import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch } from "../lib/api";
import { useAccountStatus, queryKeys } from "../lib/queries";
import { ErrorState } from "./Loading";

/**
 * The name families actually see, and the only place it can be changed.
 *
 * There were two names and no way to edit either. This page displayed the Cognito `name` claim ("Beta
 * Coach"); every family-facing surface reads `name` off the DynamoDB account item ("Coach Sam") — the
 * invite email, the signup landing page, and the coach's name in a message thread. So a coach could
 * read one name on their own settings page while sending emails signed with a different one, and had no
 * way to correct either.
 *
 * The account item wins, because it is the one families see. Saving here changes the invite emails and
 * thread labels too, since those already read from it.
 */
export function DisplayNameSetting() {
  const queryClient = useQueryClient();
  const status = useAccountStatus();
  const saved = status.data?.display_name ?? "";
  const [name, setName] = useState(saved);
  const [justSaved, setJustSaved] = useState(false);

  /**
   * Seeds the field once the account has loaded, without stamping on typing.
   *
   * Keyed on the saved value rather than run on mount: the query may still be in flight on first
   * render, and a field seeded with "" then never corrected would offer to blank the coach's name.
   */
  useEffect(() => {
    setName(saved);
  }, [saved]);

  const save = useMutation({
    mutationFn: (displayName: string) => apiPatch("/account/preferences", { displayName }),
    onSuccess: async () => {
      setJustSaved(true);
      // Re-reads rather than assuming: this same query is what the page renders the name from, and what
      // several other screens read for the coach's identity.
      await queryClient.invalidateQueries({ queryKey: queryKeys.accountStatus });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setJustSaved(false);
    save.mutate(name.trim());
  }

  const changed = name.trim() !== saved;

  return (
    <form className="plan-form" onSubmit={handleSubmit}>
      <label htmlFor="coach-display-name">Your name, as families see it</label>
      <input
        id="coach-display-name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setJustSaved(false);
        }}
        placeholder="e.g. Coach Sam"
        maxLength={120}
      />
      {/* Says where it shows up, because it is not obvious that a field on a settings page is what
          appears in the subject line of every invite. */}
      <p className="form-hint">
        This is the name on your invitation emails, on the page families land on when they accept, and
        beside your messages. Leave it blank and they just see &ldquo;your coach&rdquo;.
      </p>

      {save.isError && <ErrorState error={save.error} />}

      <button type="submit" disabled={save.isPending || !changed}>
        {save.isPending ? "Saving…" : "Save name"}
      </button>
      {justSaved && !changed && (
        <p className="badge badge-ok" role="status">
          Saved.
        </p>
      )}
    </form>
  );
}
